import { useEffect } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useAuth } from './useAuth';

// Hook for parent notifications
export const useParentNotifications = () => {
  const { subscribe, unsubscribe } = useWebSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const subscription = subscribe(
      'NotificationsChannel',
      { user_id: user.id },
      {
        received: (data) => {
          console.log('Notification received:', data);
          
          // Update notification queries
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
          
          // Show notification
          if (data.notification) {
            notifications.show({
              title: data.notification.title,
              message: data.notification.message,
              color: data.notification.priority === 'high' ? 'red' : 'blue',
            });
          }
          
          // Update unread count
          if (data.unread_count !== undefined) {
            queryClient.setQueryData(['notifications', 'parent', 'unread'], data.unread_count);
          }
        },
      }
    );

    return () => {
      if (subscription) {
        unsubscribe('NotificationsChannel');
      }
    };
  }, [user?.id, subscribe, unsubscribe, queryClient]);
};

// Hook for student activity updates
export const useStudentActivityUpdates = (studentId?: string) => {
  const { subscribe, unsubscribe } = useWebSocket();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!studentId || !user) return;

    const subscription = subscribe(
      'StudentActivityChannel',
      { 
        student_id: studentId,
        mode: 'student' 
      },
      {
        received: (data) => {
          console.log('Activity update received:', data);
          
          // Handle different message types
          switch (data.type) {
            case 'new_activity':
              // Invalidate activity queries to refetch
              queryClient.invalidateQueries({ 
                queryKey: ['parent', 'activities', studentId] 
              });
              
              // Show notification for important activities
              if (data.activity?.important) {
                notifications.show({
                  title: 'New Activity',
                  message: data.activity.description,
                  color: 'blue',
                });
              }
              break;
              
            case 'activity_verified':
              // Update specific activity in cache
              queryClient.setQueryData(
                ['parent', 'activities', studentId],
                (oldData: any) => {
                  if (!oldData) return oldData;
                  // Update the specific activity with verification status
                  return {
                    ...oldData,
                    data: oldData.data?.map((activity: any) =>
                      activity.id === data.activity_id
                        ? { ...activity, verified: true, verified_by: data.verified_by }
                        : activity
                    ),
                  };
                }
              );
              break;
              
            case 'daily_report_ready':
              // Invalidate daily report queries
              queryClient.invalidateQueries({ 
                queryKey: ['parent', 'daily-report', studentId] 
              });
              
              notifications.show({
                title: 'Daily Report Ready',
                message: 'Today\'s daily report is now available',
                color: 'green',
              });
              break;
          }
        },
      }
    );

    return () => {
      if (subscription) {
        unsubscribe('StudentActivityChannel');
      }
    };
  }, [studentId, user, subscribe, unsubscribe, queryClient]);
};

// Hook for analysis results (camera feeds)
export const useAnalysisUpdates = (cameraId?: string) => {
  const { subscribe, unsubscribe } = useWebSocket();
  // const queryClient = useQueryClient();

  useEffect(() => {
    if (!cameraId) return;

    const subscription = subscribe(
      'AnalysisChannel',
      { camera_id: cameraId },
      {
        received: (data) => {
          console.log('Analysis update received:', data);
          
          // Handle analysis results
          if (data.analysis_result) {
            // You can emit events or update specific UI components
            window.dispatchEvent(
              new CustomEvent('analysis-update', { 
                detail: data.analysis_result 
              })
            );
          }
        },
      }
    );

    return () => {
      if (subscription) {
        unsubscribe('AnalysisChannel');
      }
    };
  }, [cameraId, subscribe, unsubscribe]);
};

// Hook for all parent's children activities
export const useAllChildrenActivities = () => {
  const { subscribe, unsubscribe } = useWebSocket();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id || user.role !== 'parent' && user.role !== 'viewer') return;

    const subscription = subscribe(
      'StudentActivityChannel',
      { mode: 'all' },
      {
        received: (data) => {
          console.log('All children activity update:', data);
          
          // Invalidate all children's activities
          queryClient.invalidateQueries({ 
            queryKey: ['parent', 'activities'] 
          });
          
          // Invalidate specific child if mentioned
          if (data.student_id) {
            queryClient.invalidateQueries({ 
              queryKey: ['parent', 'activities', data.student_id] 
            });
          }
        },
      }
    );

    return () => {
      if (subscription) {
        unsubscribe('StudentActivityChannel');
      }
    };
  }, [user, subscribe, unsubscribe, queryClient]);
};